import { useState } from "react";
import { Button } from "./Button";

interface InlineNameEditorProps {
  /** Current first name — `null` or `"there"` means no name set yet. */
  firstName: string | null;
  /** Persists the name; the component handles loading/error states. */
  onSave: (firstName: string) => Promise<void>;
}

/**
 * Inline name editor used on Home, Profile and Tutor screens.
 *
 * Shows a tappable banner when no name is set. Tapping opens a single-field
 * form right in place — no navigation, no modal. The student types, taps
 * Save, and the banner disappears.
 */
export function InlineNameEditor({ firstName, onSave }: InlineNameEditorProps) {
  const needsName = !firstName || firstName === "there";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  if (!needsName && !editing) return null;

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft("");
          setEditing(true);
        }}
        className="mt-3 w-full rounded-[var(--radius-card)] border border-accent bg-accent-tint p-3 text-left text-[13px] text-accent"
      >
        Add your nickname so we know what to call you →
      </button>
    );
  }

  const save = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } catch {
      // Silent — the student can try again.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-[var(--radius-card)] border border-border bg-surface p-3">
      <label htmlFor="inline-name" className="sr-only">
        First name
      </label>
      <input
        id="inline-name"
        type="text"
        autoFocus
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") void save();
        }}
        placeholder="Your Nickname"
        maxLength={100}
        className="w-full rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-[15px] text-text outline-none placeholder:text-text-muted focus:border-accent"
      />
      <div className="flex gap-2">
        <Button
          className="!w-auto !min-h-9 px-4 text-[13px]"
          disabled={!draft.trim() || saving}
          onClick={() => void save()}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          variant="ghost"
          className="!w-auto !min-h-9 px-4 text-[13px]"
          onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
